import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import colors from '../styles/colors';
import BrazilianPattern from './BrazilianPattern';

const HeroContainer = styled.section`
  min-height: 100vh;
  background: linear-gradient(135deg, ${colors.primary.green} 0%, ${colors.primary.yellow} 50%, ${colors.primary.blue} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: visible;
  padding: 4rem 0;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="75" cy="75" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="50" cy="10" r="0.5" fill="rgba(255,255,255,0.1)"/><circle cx="10" cy="60" r="0.5" fill="rgba(255,255,255,0.1)"/><circle cx="90" cy="40" r="0.5" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
  opacity: 0.3;
`;

const Content = styled.div`
  text-align: center;
  color: white;
  z-index: 2;
  max-width: 900px;
  padding: 0 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: -1.5rem;
  margin-bottom: 4rem;
`;

const LogoContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 0;
`;

const LogoImage = styled.img`
  width: 600px;
  height: 600px;
  object-fit: contain;
  filter: drop-shadow(0 30px 70px rgba(0, 0, 0, 0.7));
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: 1200px) {
    width: 500px;
    height: 500px;
  }

  @media (max-width: 1024px) {
    width: 450px;
    height: 450px;
  }

  @media (max-width: 768px) {
    width: 400px;
    height: 400px;
  }

  @media (max-width: 480px) {
    width: 300px;
    height: 300px;
  }
`;

const LogoText = styled(motion.div)`
  h1 {
    font-size: 3rem;
    font-weight: 700;
    margin: 0;
    text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
    background: linear-gradient(135deg, 
      ${colors.primary.green} 0%, 
      ${colors.primary.yellow} 50%, 
      ${colors.primary.blue} 100%
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    @media (max-width: 768px) {
      font-size: 2.2rem;
    }
  }

  p {
    font-size: 1.2rem;
    font-weight: 500;
    margin: 0.5rem 0 0 0;
    opacity: 0.9;
    color: white;

    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`;

const Subtitle = styled(motion.h2)`
  font-size: 1.8rem;
  font-weight: 400;
  margin-top: -2.5rem;
  margin-bottom: 0;
  opacity: 0.9;
  color: white;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.3);

  @media (max-width: 1024px) {
    font-size: 1.6rem;
    margin-top: -2rem;
  }

  @media (max-width: 768px) {
    font-size: 1.4rem;
    margin-top: -1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1.2rem;
    margin-top: -1rem;
  }
`;

const Description = styled(motion.p)`
  font-size: 1.2rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  opacity: 0.9;
  max-width: 700px;
  color: white;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.3);

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const CTAButton = styled(motion.button)`
  background: white;
  color: ${colors.primary.green};
  border: none;
  padding: 1rem 2.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
  margin-top: 1rem;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(0,0,0,0.3);
    background: ${colors.primary.green};
    color: white;
  }

  &:active {
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    padding: 0.8rem 2rem;
    font-size: 1rem;
    margin-top: 0.8rem;
  }

  @media (max-width: 480px) {
    padding: 0.7rem 1.5rem;
    font-size: 0.9rem;
    margin-top: 0.6rem;
  }
`;


const Hero = () => {
  const { t } = useTranslation();

  const handleReservation = () => {
    const whatsappNumber = "393331054944";
    const message = "Ciao! Vorrei prenotare un tavolo al Cuore Brasiliano. Grazie!";
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <HeroContainer id="home">
      <BackgroundPattern />
      <BrazilianPattern />

      <Content>
        <LogoContainer
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <LogoImage 
            src="./logo.png" 
            alt="Cuore Brasiliano" 
          />
        </LogoContainer>
        
        <Subtitle
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {t('hero.subtitle')}
        </Subtitle>
        
        <CTAButton
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleReservation}
        >
          {t('hero.cta')}
        </CTAButton>
      </Content>
    </HeroContainer>
  );
};

export default Hero;
